import { DirectLinkSource, PERSISTENT_ONSITE_ENTRYPOINT_SOURCES } from '../constants'
import { isEligibleForOnsiteTherapy } from '../onsiteTherapyUtils'

export type OnsiteTherapyVisibilityParams = {
  isOnsiteEntrypointsEnabled: boolean
  isOnsiteEligibleResponseEnabled: boolean
  onsiteProvidersCount: number | undefined
  customerDisplayName: string | null
  isOnsiteEligible?: boolean
  eligibleOnsiteAddressCount?: number
}

export const computeOnsiteTherapyVisibility = ({
  isOnsiteEntrypointsEnabled,
  isOnsiteEligibleResponseEnabled,
  onsiteProvidersCount,
  customerDisplayName,
  isOnsiteEligible,
  eligibleOnsiteAddressCount,
}: OnsiteTherapyVisibilityParams) => {
  if (!isEligibleForOnsiteTherapy({ isOnsiteEligibleResponseEnabled, isOnsiteEligible, eligibleOnsiteAddressCount })) {
    return { hasOnsiteProviders: false, showPersistentOnsiteEntrypointSadPath: false, showOnsiteTherapy: false }
  }

  const hasOnsiteProviders = (onsiteProvidersCount ?? 0) > 0
  // Only true when providers have been loaded AND the list is empty — not when loading (undefined)
  const showPersistentOnsiteEntrypointSadPath = isOnsiteEntrypointsEnabled && onsiteProvidersCount === 0
  const showOnsiteTherapy = Boolean(hasOnsiteProviders && customerDisplayName) || showPersistentOnsiteEntrypointSadPath

  return { hasOnsiteProviders, showPersistentOnsiteEntrypointSadPath, showOnsiteTherapy }
}

export const useOnsiteTherapyVisibility = (params: OnsiteTherapyVisibilityParams) =>
  computeOnsiteTherapyVisibility(params)

export type PersistentOnsiteEntrypointParams = {
  isOnsiteEligibleResponseEnabled: boolean
  isOnsiteEligible?: boolean
  isOnsiteEntrypointsEnabled: boolean
  onsiteProvidersCount: number | undefined
  directLinkSource?: DirectLinkSource
  isDirectPath: boolean
  loading?: boolean
}

/**
 * Determines if user is in the persistent onsite entrypoint sad path scenario.
 * This is when:
 * - User came from Homebase, Benefits page, or Integrated Benefits forked triage via direct path
 * - User has 0 onsite providers available (not loading)
 * - User is eligible for onsite (or eligibility feature is disabled)
 */
export const computePersistentOnsiteEntrypointSadPath = ({
  isOnsiteEligibleResponseEnabled,
  isOnsiteEligible,
  isOnsiteEntrypointsEnabled,
  onsiteProvidersCount,
  directLinkSource,
  isDirectPath,
  loading = false,
}: PersistentOnsiteEntrypointParams): boolean => {
  // Check eligibility
  if (isOnsiteEligibleResponseEnabled && isOnsiteEligible === false) {
    return false
  }

  // Check if came from persistent entrypoints (Homebase, Benefits, or Integrated Benefits forked triage)
  const isPersistentEntrypoint =
    isOnsiteEntrypointsEnabled &&
    isDirectPath &&
    directLinkSource !== undefined &&
    PERSISTENT_ONSITE_ENTRYPOINT_SOURCES.includes(directLinkSource)

  // Check if providers have been loaded and list is empty
  const hasNoOnsiteProviders = !loading && onsiteProvidersCount === 0

  return isPersistentEntrypoint && hasNoOnsiteProviders
}
